import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/pt'

/**
 * The drafted push for an applied update (D-15, DESK-04, UI-SPEC §Copy H).
 *
 * After Dominique applies an update proposal, the existing
 * `SendPushToBookmarks` modal opens pre-filled with one en and one pt-PT
 * message. This picks which draft to use and fills it in.
 *
 * Priority: status (cancelled / postponed) > date > place > generic. A change
 * counts only when the candidate carries the new value: a start change with no
 * start date, or a place change with a blank place, falls through to the next
 * rule.
 *
 * `{{title}}` is the event title in the field's language (`title` for en,
 * `title_pt` for pt-PT), falling back to the other. `{{date}}` is
 * `ddd D MMM, HH:mm` in that language's dayjs locale. The candidate start is a
 * wall-clock Date (the poster's digits in local time, the same rule as
 * compareRows.ts), formatted with no zone conversion.
 *
 * The drafts reach app users, so they carry no counts or statistics (owner
 * ruling no-event-statistics-in-copy): the only digits are the date's own.
 *
 * Pure: `t` is injected, so the selection is tested without i18next. A caller
 * using i18next passes `(key, vars, lng) => i18n.t(key, { ...vars, lng })`.
 */

/** Structurally identical to compareRows' `CompareField` (33-07). */
export type PushChange = 'title' | 'start' | 'end' | 'place' | 'price' | 'status'

export type PushLanguage = 'en' | 'pt-PT'

export type PushT = (key: string, vars: Record<string, string>, lng: PushLanguage) => string

export type PushDraftEvent = {
  title: string | null
  title_pt: string | null
}

export type PushDraftCandidate = {
  status: 'cancelled' | 'postponed' | 'scheduled'
  start: Date | null
  place: string | null
}

export type BuildPushDraftsParams = {
  changes: PushChange[]
  event: PushDraftEvent
  candidate: PushDraftCandidate
  t: PushT
}

export type PushDrafts = {
  en: string
  pt: string
}

export const PUSH_DATE_FORMAT = 'ddd D MMM, HH:mm'

const DAYJS_LOCALE: Record<PushLanguage, string> = {
  en: 'en',
  'pt-PT': 'pt'
}

const clean = (value: string | null | undefined) => (value ?? '').trim()

const titleFor = (event: PushDraftEvent, lng: PushLanguage) => {
  const own = lng === 'en' ? event.title : event.title_pt
  const other = lng === 'en' ? event.title_pt : event.title
  return clean(own) || clean(other)
}

type Draft = { key: string; vars: (lng: PushLanguage) => Record<string, string> }

const chooseDraft = (changes: PushChange[], candidate: PushDraftCandidate): Draft => {
  const changed = new Set(changes)
  const place = clean(candidate.place)
  const start = candidate.start

  if (changed.has('status') && candidate.status === 'cancelled') {
    return { key: 'push.draft.cancelled', vars: () => ({}) }
  }
  if (changed.has('status') && candidate.status === 'postponed') {
    return { key: 'push.draft.postponed', vars: () => ({}) }
  }
  if (changed.has('start') && start) {
    return {
      key: 'push.draft.new_date',
      vars: (lng) => ({ date: dayjs(start).locale(DAYJS_LOCALE[lng]).format(PUSH_DATE_FORMAT) })
    }
  }
  if (changed.has('place') && place) {
    return { key: 'push.draft.new_place', vars: () => ({ place }) }
  }
  return { key: 'push.draft.generic', vars: () => ({}) }
}

export const buildPushDrafts = ({ changes, event, candidate, t }: BuildPushDraftsParams): PushDrafts => {
  const draft = chooseDraft(changes, candidate)
  const write = (lng: PushLanguage) =>
    t(draft.key, { title: titleFor(event, lng), ...draft.vars(lng) }, lng)

  return { en: write('en'), pt: write('pt-PT') }
}
